import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Info, UserCog } from 'lucide-react';
import { toast } from 'sonner';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { PAPEIS } from '@/nucleo/auth/papeis';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { FormPage } from '@/ui/FormPage';
import { FormSection } from '@/ui/FormSection';
import { PageHeader } from '@/ui/PageHeader';
import { Alert, AlertDescription } from '@/ui/base/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/base/alert-dialog';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { CampoDePerfil } from './CampoDePerfil';
import type { DetalheDoUsuario } from './contrato';
import { novaChave, useAcaoDaConta, useDetalheDoUsuario, useOpcoes } from './dados';
import { rotuloDoPerfil, rotuloDosPerfis } from './rotulos';

const PERFIS_TEG_E_DRE = PAPEIS.filter(
  (p) =>
    ![
      'CREDENCIADO',
      'ADMIN_CREDENCIADO',
      'OPERADOR_CREDENCIADO',
      'FINANCEIRO_CREDENCIADO',
    ].includes(p),
);
const PERFIS_DE_EMPRESA = ['ADMIN_CREDENCIADO', 'OPERADOR_CREDENCIADO', 'FINANCEIRO_CREDENCIADO'];

/** Para onde a conta pode ir: a mesma regra de `regras.ts` da API, que confere de novo. */
function perfisPermitidos(pessoa: DetalheDoUsuario): readonly string[] {
  if (pessoa.perfis.includes('CREDENCIADO')) return [];
  return pessoa.tenant === 'CREDENCIADO' ? PERFIS_DE_EMPRESA : PERFIS_TEG_E_DRE;
}

/** Índice menor, perfil mais alto (a ordem de precedência dos papéis). */
function nivel(perfil: string): number {
  const i = (PAPEIS as readonly string[]).indexOf(perfil);
  return i === -1 ? PAPEIS.length : i;
}

function sentido(de: readonly string[], para: string): 'NENHUMA' | 'ELEVACAO' | 'REBAIXAMENTO' {
  if (de.length === 1 && de[0] === para) return 'NENHUMA';
  return nivel(para) < Math.min(...de.map(nivel), PAPEIS.length) ? 'ELEVACAO' : 'REBAIXAMENTO';
}

/**
 * `/admin/usuarios/:id/editar`: nome, perfil de acesso e diretoria regional.
 *
 * O e-mail não muda por aqui: é o endereço de entrada e o destino do código do
 * segundo fator, e trocá-lo com segurança pede confirmar o endereço novo antes
 * — fluxo que o sistema ainda não tem. Mudar o perfil pede confirmação, com o
 * sentido escrito (elevação ou rebaixamento), e derruba as sessões da pessoa
 * para o perfil novo valer.
 */
export function UsuarioEditarPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detalhe = useDetalheDoUsuario(id);
  const opcoes = useOpcoes();
  const salvar = useAcaoDaConta(id);
  const [nome, setNome] = useState('');
  const [perfil, setPerfil] = useState('');
  const [dreId, setDreId] = useState('');
  const [erros, setErros] = useState<{ nome?: string; perfil?: string; recorte?: string }>({});
  const [falha, setFalha] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const chave = useRef(novaChave());
  const carregado = useRef(false);

  useEffect(() => {
    const p = detalhe.data;
    if (!p || carregado.current) return;
    carregado.current = true;
    setNome(p.nome);
    setPerfil(p.perfis.length === 1 ? p.perfis[0]! : '');
    setDreId(p.tenant === 'DRE' ? (p.recorte?.id ?? '') : '');
  }, [detalhe.data]);

  const pessoa = detalhe.data;
  const mudanca = pessoa && perfil ? sentido(pessoa.perfis, perfil) : 'NENHUMA';
  const ehDre = opcoes.data?.perfis.find((p) => p.codigo === perfil)?.tenant === 'DRE';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setFalha(null);
    const e: typeof erros = {};
    if (nome.trim().split(/\s+/).length < 2) e.nome = 'Informe o nome completo.';
    if (!perfil) e.perfil = 'Escolha o perfil de acesso.';
    if (ehDre && !dreId) e.recorte = 'Escolha a diretoria regional.';
    setErros(e);
    if (Object.keys(e).length > 0) return;
    if (mudanca !== 'NENHUMA') setConfirmando(true);
    else gravar(false);
  }

  function gravar(confirmado: boolean) {
    salvar.mutate(
      {
        acao: 'editar',
        corpo: {
          nome: nome.trim(),
          perfil,
          ...(ehDre && dreId ? { dreId } : {}),
          ...(confirmado ? { confirmarMudancaDePerfil: true } : {}),
          chaveIdempotencia: chave.current,
        },
      },
      {
        onSuccess: (r) => {
          toast.success(r.mensagem);
          navigate(`/admin/usuarios/${id}`);
        },
        onError: (erro) =>
          setFalha(erro instanceof ErroApi ? erro.aviso : 'Não foi possível salvar as alterações.'),
      },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Editar pessoa" description={pessoa?.nome} />
      <EstadoDaTela
        carregando={detalhe.isPending || opcoes.isPending}
        erro={detalhe.error ?? opcoes.error}
        aoTentarDeNovo={() => {
          void detalhe.refetch();
          void opcoes.refetch();
        }}
        descricao="os dados da pessoa"
      >
        {pessoa && opcoes.data && !pessoa.acoes.editar && (
          <Alert>
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              {pessoa.restricao ?? 'Esta conta não pode ser editada por aqui.'}
            </AlertDescription>
          </Alert>
        )}
        {pessoa && opcoes.data && pessoa.acoes.editar && (
          <FormPage
            onSubmit={enviar}
            isSaving={salvar.isPending}
            submitLabel={salvar.isPending ? 'Salvando' : 'Salvar'}
            backTo={`/admin/usuarios/${id}`}
            maxWidth="max-w-4xl"
          >
            {falha && (
              <Alert variant="destructive">
                <AlertDescription>{falha}</AlertDescription>
              </Alert>
            )}
            <FormSection title="Dados" icon={UserCog}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="editar-nome">Nome completo</Label>
                  <Input
                    id="editar-nome"
                    value={nome}
                    aria-invalid={Boolean(erros.nome)}
                    aria-describedby={erros.nome ? 'editar-nome-erro' : undefined}
                    onChange={(e) => {
                      chave.current = novaChave();
                      setNome(e.target.value);
                    }}
                  />
                  {erros.nome && (
                    <p id="editar-nome-erro" className="text-sm text-destructive-text">
                      {erros.nome}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editar-email">E-mail</Label>
                  <Input
                    id="editar-email"
                    value={pessoa.email}
                    readOnly
                    aria-describedby="editar-email-ajuda"
                  />
                  <p id="editar-email-ajuda" className="text-xs text-muted-foreground">
                    O e-mail é o endereço de entrada e não muda por aqui.
                  </p>
                </div>
              </div>
              {pessoa.perfis.length > 1 && (
                <p className="mt-4 text-sm text-muted-foreground">
                  Hoje a conta tem {rotuloDosPerfis(pessoa.perfis)}. Ao salvar, ela fica só com o
                  perfil escolhido abaixo.
                </p>
              )}
            </FormSection>
            <FormSection title="Perfil de acesso">
              <CampoDePerfil
                opcoes={opcoes.data}
                permitidos={perfisPermitidos(pessoa)}
                perfil={perfil}
                recorteId={dreId}
                aoMudarPerfil={(p) => {
                  chave.current = novaChave();
                  setPerfil(p);
                  if (opcoes.data?.perfis.find((x) => x.codigo === p)?.tenant !== 'DRE')
                    setDreId('');
                }}
                aoMudarRecorte={(r) => {
                  chave.current = novaChave();
                  setDreId(r);
                }}
                erroDoPerfil={erros.perfil}
                erroDoRecorte={erros.recorte}
                empresaFixa={
                  pessoa.tenant === 'CREDENCIADO' ? (pessoa.recorte?.nome ?? null) : undefined
                }
              />
            </FormSection>
          </FormPage>
        )}
      </EstadoDaTela>

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mudanca === 'ELEVACAO'
                ? 'Confirmar elevação de perfil'
                : 'Confirmar rebaixamento de perfil'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pessoa?.nome} passa de {rotuloDosPerfis(pessoa?.perfis ?? [])} para{' '}
              {rotuloDoPerfil(perfil)}.{' '}
              {mudanca === 'ELEVACAO'
                ? 'A pessoa passa a ver e fazer mais no sistema.'
                : 'A pessoa perde acesso ao que o perfil atual permite.'}{' '}
              As sessões abertas dela são encerradas para o perfil novo valer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmando(false);
                gravar(true);
              }}
            >
              {mudanca === 'ELEVACAO' ? 'Confirmar elevação' : 'Confirmar rebaixamento'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default UsuarioEditarPage;
